import {
  useMutation,
  type ApolloCache,
  type DefaultContext,
  type DocumentNode,
  type MutationFunctionOptions,
  type MutationHookOptions,
  type MutationTuple,
  type OperationVariables,
  type TypedDocumentNode,
} from "@apollo/client";
import type { OperationDefinitionNode } from "graphql";

const useMutationWrapper = <
  TData = unknown,
  TVariables extends OperationVariables = OperationVariables,
  TContext extends DefaultContext = DefaultContext,
  TCache extends ApolloCache<unknown> = ApolloCache<unknown>
>(
  mutation: DocumentNode | TypedDocumentNode<TData, TVariables>,
  options: MutationHookOptions<TData, TVariables, TContext, TCache> = {}
): MutationTuple<TData, TVariables, TContext, TCache> => {
  const [mutationFunc, statusInfo] = useMutation<
    TData,
    TVariables,
    TContext,
    TCache
  >(mutation, options);
  const { refetchQueries = [] } = options;
  // `refetchQueries` can also be a function; the runtime only maps the array form.
  const refetchQueryList: Array<string | DocumentNode> = Array.isArray(
    refetchQueries
  )
    ? (refetchQueries as Array<string | DocumentNode>)
    : [];
  if (refetchQueryList.length === 0) {
    return [mutationFunc, statusInfo];
  } else {
    return [
      (
        deeperOptions: MutationFunctionOptions<
          TData,
          TVariables,
          TContext,
          TCache
        > = {}
      ) =>
        mutationFunc({
          ...(options ? options : {}),
          ...deeperOptions,
          context: {
            ...(options.context ? options.context : {}),
            ...(deeperOptions.context ? deeperOptions.context : {}),
            refetchQueries: refetchQueryList.map((refetchQuery) => {
              if (typeof refetchQuery === "string") {
                return refetchQuery;
              } else {
                return (
                  refetchQuery?.definitions?.[0] as
                    | OperationDefinitionNode
                    | undefined
                )?.name?.value;
              }
            }),
          },
        } as unknown as MutationFunctionOptions<
          TData,
          TVariables,
          TContext,
          TCache
        >),
      statusInfo,
    ];
  }
};

export default useMutationWrapper;
